import type { EmailLengthText, GeneratedEmail, Reaction } from '../../domain/generatedEmail';
import type { Insight } from '../../domain/insight';
import type { AdNetwork, Opportunity } from '../../domain/opportunity';
import type { Quota } from '../../domain/quota';
import type { SiteTrend } from '../../domain/siteTrend';
import { BASE_API_URL, HttpClient } from '../../services/HttpClient';
import { snakeToCamel } from '../../services/snakeToCamel';

export type GenerateEmailReceiver = {
  companyName: string;
  contactName?: string;
  contactRole?: string;
  companyDescription?: string;
  companyWebsite: string;
};

export type GenerateEmailPayload = {
  textLength: EmailLengthText;
  receiver: GenerateEmailReceiver;
  siteTrendsInsightModels: Insight[];
  opportunitiesInsightModels: Insight[];
  technologySignalInsightModels: Insight[];
  trafficSignalInsightModels: Insight[];
  newsSignalInsightModels: Insight[];
  adNetworkSignalInsightModels: Insight[];
  newMarketsSignalInsightModels: Insight[];
  languageCode: string;
};

export type GeneratedEmailDto = {
  value_proposition: string;
  business_affect: string;
  business_challenge: string;
  insights: string;
};

export type GenerateEmailResponseDto = { email: GeneratedEmailDto; requestId: number };

export type GenerateEmailResponse = { email: GeneratedEmail; requestId: number; textLength: EmailLengthText };

const INSIGHTS_API_URL = '/sales-extension/api/insights';

export const insightsApi = {
  getSiteTrends: (companyDomain: string) => {
    const searchParams = new URLSearchParams({ domain: companyDomain });
    return HttpClient.get<Omit<SiteTrend, 'insightType' | 'id'>[]>(
      `${INSIGHTS_API_URL}/site-trends?${searchParams.toString()}`
    );
  },

  getOpportunities: (companyDomain: string) => {
    const searchParams = new URLSearchParams({ domain: companyDomain });

    return HttpClient.get<Omit<Opportunity, 'insightType' | 'id'>[]>(
      `${INSIGHTS_API_URL}/opportunities?${searchParams.toString()}`
    );
  },

  getAdNetworks: (companyDomain: string, signal?: AbortSignal) => {
    const searchParams = new URLSearchParams({ domain: companyDomain });

    return HttpClient.get<{ adNetworks: AdNetwork[]; date: string }>(
      `/sales-extension/api/ad-networks?${searchParams.toString()}`,
      { signal }
    );
  },

  trackCopy: (requestId: number) => {
    return HttpClient.put(`${BASE_API_URL}/email-generator/request/${requestId}/track-copy`, undefined);
  },
  trackLike: (requestId: number, reaction: Reaction) => {
    const hasLiked = reaction === 'like';
    return HttpClient.put(
      `${BASE_API_URL}/email-generator/request/${requestId}/track-like?isLiked=${hasLiked.toString()}`,
      undefined
    );
  },
  generateEmail: async (payload: GenerateEmailPayload, sessionId: string) => {
    const { email, requestId } = await HttpClient.post<GenerateEmailResponseDto>(
      `${BASE_API_URL}/email-generator/generate?sessionId=${sessionId}`,
      payload
    );

    return { email: snakeToCamel(email), requestId, textLength: payload.textLength } as GenerateEmailResponse;
  },

  fetchEmailsQuota: async (): Promise<Quota> => {
    const quota = await HttpClient.get<Quota>(`${BASE_API_URL}/email-generator/quota`);
    return quota;
  },
};
